import { sortFeeTiers } from "./fusionFeeUtils";

export const LEGACY_IAR_FEE_SCHEDULE_NAME = "Default Schedule";

const normalizeLabel = (value) => String(value ?? "").trim();
const normalizeTierLabel = (tier) =>
  tier && typeof tier === "object"
    ? normalizeLabel(tier.label)
    : normalizeLabel(tier);

const normalizeTierRow = (tier) => ({
  label: normalizeTierLabel(tier),
  rate: tier && typeof tier === "object" ? tier.rate ?? "" : "",
});

function getConfiguredScheduleNames(customFees) {
  const configured = Object.keys(customFees?.feeSchedules || {});
  const requested = Array.isArray(customFees?.scheduleOrder)
    ? customFees.scheduleOrder.map(normalizeLabel).filter(Boolean)
    : [];
  return [
    ...requested.filter((name, index) =>
      configured.includes(name) && requested.indexOf(name) === index
    ),
    ...configured.filter((name) => !requested.includes(name)),
  ];
}

export function getIarFeeScheduleRows(customFees) {
  if (!customFees || typeof customFees !== "object") return {};

  if (
    customFees.feeSchedules &&
    typeof customFees.feeSchedules === "object" &&
    !Array.isArray(customFees.feeSchedules)
  ) {
    const schedules = {};
    getConfiguredScheduleNames(customFees).forEach((name) => {
      const tiers = customFees.feeSchedules[name];
      const scheduleName = normalizeLabel(name);
      if (!scheduleName || !Array.isArray(tiers)) return;

      const rows = sortFeeTiers(
        tiers.map(normalizeTierRow).filter((tier) => tier.label),
      );
      if (rows.length > 0) schedules[scheduleName] = rows;
    });
    if (Object.keys(schedules).length > 0) return schedules;
  }

  // Older records stored one unnamed tiers array. Give it a neutral internal
  // name instead of claiming it is "Schedule 1"; a single schedule name is not
  // shown to users anyway.
  if (Array.isArray(customFees.tiers)) {
    const rows = sortFeeTiers(
      customFees.tiers
        .map(normalizeTierRow)
        .filter((tier) => tier.label),
    );
    if (rows.length > 0) {
      return { [LEGACY_IAR_FEE_SCHEDULE_NAME]: rows };
    }
  }

  return {};
}

export function getIarFeeSchedules(customFees) {
  return Object.fromEntries(
    Object.entries(getIarFeeScheduleRows(customFees)).map(([name, rows]) => [
      name,
      rows.map((row) => row.label),
    ]),
  );
}

export function resolveIarFeeSchedule(customFees, requestedName = "") {
  const scheduleRows = getIarFeeScheduleRows(customFees);
  const schedules = Object.fromEntries(
    Object.entries(scheduleRows).map(([name, rows]) => [
      name,
      rows.map((row) => row.label),
    ]),
  );
  const scheduleNames = Object.keys(scheduleRows);
  const selectedName = Object.prototype.hasOwnProperty.call(
    schedules,
    requestedName,
  )
    ? requestedName
    : scheduleNames.length === 1
      ? scheduleNames[0]
      : "";

  return {
    schedules,
    scheduleNames,
    selectedName,
    labels: selectedName ? schedules[selectedName] || [] : [],
    rows: selectedName ? scheduleRows[selectedName] || [] : [],
  };
}

export function scheduleLabelsMatch(value, labels) {
  if (!Array.isArray(value) || !Array.isArray(labels)) return false;
  if (value.length !== labels.length) return false;

  return labels.every(
    (label, index) => normalizeLabel(value[index]?.label) === normalizeLabel(label),
  );
}

export function reconcileTieredRatesForSchedule(value, labels, defaults = []) {
  const safeLabels = Array.isArray(labels) ? labels : [];
  if (safeLabels.length === 0) return [];

  if (scheduleLabelsMatch(value, safeLabels)) {
    return safeLabels.map((label, index) => ({
      label,
      rate: value[index]?.rate ?? "",
    }));
  }

  // A legacy label-keyed object can be retained only when it describes this
  // exact schedule. Partial overlap is deliberately rejected so copied rates
  // from another office cannot be silently relabeled onto the current office.
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const valueLabels = Object.keys(value).map(normalizeLabel).sort();
    const scheduleLabels = [...new Set(safeLabels.map(normalizeLabel))].sort();
    if (
      valueLabels.length === scheduleLabels.length &&
      valueLabels.every((label, index) => label === scheduleLabels[index])
    ) {
      const consumed = Object.create(null);
      return safeLabels.map((label) => {
        if (!consumed[label] && value[label] != null) {
          consumed[label] = true;
          return { label, rate: value[label] };
        }
        return { label, rate: "" };
      });
    }
  }

  if (scheduleLabelsMatch(defaults, safeLabels)) {
    return safeLabels.map((label, index) => ({
      label,
      rate: defaults[index]?.rate ?? "",
    }));
  }

  return safeLabels.map((label) => ({ label, rate: "" }));
}

export function isCustomFeeResponseAuthorizedForAdvisor(customFees, advisorName) {
  if (!customFees || typeof customFees !== "object") return true;
  const assignedAdvisor = normalizeLabel(customFees.advisorId);
  if (!assignedAdvisor) return true; // Backward-compatible response/mocks.
  if (assignedAdvisor.toLowerCase() === "all") return true;
  return assignedAdvisor === normalizeLabel(advisorName);
}

export function getVisibleIarFeeScheduleName(customFees, selectedName) {
  const { scheduleNames } = resolveIarFeeSchedule(customFees, selectedName);
  return scheduleNames.length > 1 && scheduleNames.includes(selectedName)
    ? selectedName
    : "";
}
